/**
 * Lead taxonomy — the single source of truth for lead state.
 *
 * Two-tier model (confirmed with product):
 *  - `status` is a HARDCODED GLOBAL ENUM. One vocabulary, shared by the UI now
 *    and the backend later. Never redefine per module.
 *  - `substatus` is a PER-MODULE internal enum — each module (Sales
 *    Intelligence, each AI Worker type) owns its own substatus vocabulary,
 *    declared in its config. Not globally fixed.
 *  - `outcome` is a separate DB column (not nested) carrying the reason/result.
 *
 * The full funnel for Sales Intelligence is the statuses below. Every other
 * stage name a user might think of (Icebreaker, Follow-up, Meeting, Pilot…) is
 * a SUBSTATUS of one of these, never a status — so Kanban columns stay at 6 and
 * never sprawl. AI Worker modules define their OWN status sequence in config.
 *
 * COLORS are NOT redefined here — they are pulled from the global
 * `STATUS_MAP` design-token dictionary (utils/statusMapping.ts) so Sales
 * Intelligence and Leads Intelligence always render the same color for the same
 * status. Green/Emerald = CONVERTED; Engaged = Pink (a mid-funnel step), etc.
 */

import { STATUS_MAP } from '../../utils/statusMapping';

export type LeadStatusKey =
  | 'leads'
  | 'enriched'
  | 'outreaching'
  | 'outreached'
  | 'engaged'
  | 'proposal'
  | 'converted'
  | 'discarded';

export interface LeadStatusDef {
  key: LeadStatusKey;
  label: string;
  /** Funnel order (0 = top of funnel). Used to sequence Kanban columns. */
  order: number;
  /** Tailwind text color class for headings/dots. */
  color: string;
  /** Canonical status accent hex, used for non-Tailwind computed surfaces. */
  accentColor: string;
  /** Solid dot background class. */
  dot: string;
  /** Soft pill class. */
  pill: string;
  /** iconify icon. */
  icon: string;
}

// Map a UniBox lead-status key to its equivalent key in the global STATUS_MAP
// (the canonical color dictionary). `leads` ↔ `new_lead`; the rest share keys.
const globalStatusKeyFor = (key: LeadStatusKey) =>
  key === 'leads' ? 'new_lead' : key;

// Build the pill class from the global STATUS_MAP tokens so the soft pill
// styling stays in lock-step with the canonical bg/border/text colors.
const pillFromGlobal = (key: LeadStatusKey): string => {
  const g = STATUS_MAP[globalStatusKeyFor(key)];
  return `${g.tailwindBorder} ${g.tailwindBg} ${g.tailwindColor}`;
};

// THE global enum. Colors are derived from STATUS_MAP — never hardcode a hex or
// tailwind color for a status here; change it in utils/statusMapping.ts instead.
export const LEAD_STATUS: Record<LeadStatusKey, LeadStatusDef> = {
  leads: {
    key: 'leads',
    label: 'Leads',
    order: 0,
    color: STATUS_MAP.new_lead.tailwindColor,
    accentColor: STATUS_MAP.new_lead.color,
    dot: STATUS_MAP.new_lead.dotColor,
    pill: pillFromGlobal('leads'),
    icon: 'bx:user',
  },
  enriched: {
    key: 'enriched',
    label: 'Enriched',
    order: 1,
    color: STATUS_MAP.enriched.tailwindColor,
    accentColor: STATUS_MAP.enriched.color,
    dot: STATUS_MAP.enriched.dotColor,
    pill: pillFromGlobal('enriched'),
    icon: 'bx:planet',
  },
  outreaching: {
    key: 'outreaching',
    label: 'Outreaching',
    order: 2,
    color: STATUS_MAP.outreaching.tailwindColor,
    accentColor: STATUS_MAP.outreaching.color,
    dot: STATUS_MAP.outreaching.dotColor,
    pill: pillFromGlobal('outreaching'),
    icon: 'bx:paper-plane',
  },
  // OUTREACHED is now a top-level STATUS (substatus: "awaiting"), between
  // OUTREACHING and ENGAGED in the funnel.
  outreached: {
    key: 'outreached',
    label: 'Outreached',
    order: 3,
    color: STATUS_MAP.outreached.tailwindColor,
    accentColor: STATUS_MAP.outreached.color,
    dot: STATUS_MAP.outreached.dotColor,
    pill: pillFromGlobal('outreached'),
    icon: 'bx:paper-plane',
  },
  engaged: {
    key: 'engaged',
    label: 'Engaged',
    order: 4,
    color: STATUS_MAP.engaged.tailwindColor,
    accentColor: STATUS_MAP.engaged.color,
    dot: STATUS_MAP.engaged.dotColor,
    pill: pillFromGlobal('engaged'),
    icon: 'bx:conversation',
  },
  proposal: {
    key: 'proposal',
    label: 'Proposal',
    order: 5,
    color: STATUS_MAP.proposal.tailwindColor,
    accentColor: STATUS_MAP.proposal.color,
    dot: STATUS_MAP.proposal.dotColor,
    pill: pillFromGlobal('proposal'),
    icon: 'bx:file-blank',
  },
  converted: {
    key: 'converted',
    label: 'Converted',
    order: 6,
    color: STATUS_MAP.converted.tailwindColor,
    accentColor: STATUS_MAP.converted.color,
    dot: STATUS_MAP.converted.dotColor,
    pill: pillFromGlobal('converted'),
    icon: 'bx:trophy',
  },
  discarded: {
    key: 'discarded',
    label: 'Discarded',
    order: 7,
    color: STATUS_MAP.discarded.tailwindColor,
    accentColor: STATUS_MAP.discarded.color,
    dot: STATUS_MAP.discarded.dotColor,
    pill: pillFromGlobal('discarded'),
    icon: 'bx:trash',
  },
};

/** All statuses in funnel order. */
export const FUNNEL_ORDER: LeadStatusKey[] = (Object.values(LEAD_STATUS)
  .sort((a, b) => a.order - b.order) as LeadStatusDef[]).map((s) => s.key);

/** Module-facing substatus vocabulary for a given status (per-module, not global). */
export interface SubstatusDef {
  key: string;
  label: string;
  /** iconify icon shown in grouped headers and cards. */
  icon?: string;
  /** True for system-owned transitional processing states (label ends "…") —
   *  rendered for information, never user-selectable. */
  transitional?: boolean;
}

/** Module-facing outcome vocabulary (orthogonal DB column). */
export interface OutcomeDef {
  key: string;
  label: string;
}

/**
 * Sales Intelligence substatus vocabulary, keyed by stage. This is the
 * per-module internal enum — it lives in config (see salesConfig), not in the
 * global enum. Other modules declare their own.
 */
export const SALES_SUBSTATUS_BY_STAGE: Record<LeadStatusKey, SubstatusDef[]> = {
  leads: [
    { key: 'ready', label: 'Ready', icon: 'bx:check-circle' },
  ],
  enriched: [
    { key: 'ready', label: 'Ready', icon: 'bx:check-circle' },
    // Transitional (…) — system-owned processing states, never user-selectable.
    { key: 'writting_message', label: 'Writing message…', icon: 'bx:pen', transitional: true },
    // `message_ready` and `ready_to_outreach` are the SAME user-visible state.
    { key: 'message_ready', label: 'Ready to Outreach', icon: 'bx:envelope-open' },
    { key: 'ready_to_outreach', label: 'Ready to Outreach', icon: 'bx:envelope-open' },
  ],
  outreaching: [
    { key: 'not_started', label: 'Not started', icon: 'bx:pause-circle' },
    { key: 'icebreaker', label: 'Icebreaker', icon: 'bx:message-dots' },
    { key: 'follow_up', label: 'Follow-up', icon: 'bx:redo' },
    { key: 'last_message', label: 'Last message', icon: 'bx:send' },
  ],
  // OUTREACHED is now a top-level STATUS with exactly one substatus.
  outreached: [
    { key: 'awaiting', label: 'Awaiting', icon: 'bx:time-five' },
  ],
  engaged: [
    { key: 'reply_received', label: 'Reply received', icon: 'bx:reply' },
    { key: 'in_conversation', label: 'In conversation', icon: 'bx:conversation' },
    { key: 'meeting_requested', label: 'Meeting requested', icon: 'bx:calendar-plus' },
    { key: 'meeting_scheduled', label: 'Meeting scheduled', icon: 'bx:calendar-check' },
    { key: 'discovery_completed', label: 'Discovery completed', icon: 'bx:check-circle' },
    // AI-assigned: when the lead is qualified and scored, the agent sets this
    // substatus and writes opportunity_value onto the lead (see UniBoxEntity).
    { key: 'qualified', label: 'Qualified', icon: 'bx:badge-check' },
    { key: 'objection', label: 'Objection raised', icon: 'bx:error-circle' },
  ],
  proposal: [
    { key: 'proposal_prepared', label: 'Proposal prepared', icon: 'bx:file-blank' },
    { key: 'review_scheduled', label: 'Review scheduled', icon: 'bx:calendar' },
    { key: 'objections_resolved', label: 'Objections resolved', icon: 'bx:check-shield' },
    { key: 'decision_process_identified', label: 'Decision process identified', icon: 'bx:git-branch' },
    { key: 'next_action_scheduled', label: 'Next action scheduled', icon: 'bx:calendar-plus' },
  ],
  converted: [
    { key: 'closed_won', label: 'Closed-won', icon: 'bx:trophy' },
    { key: 'onboarding', label: 'Onboarding', icon: 'bx:rocket' },
    { key: 'active', label: 'Active', icon: 'bx:check-shield' },
    { key: 'upsell', label: 'Upsell opportunity', icon: 'bx:trending-up' },
    { key: 'support', label: 'Support needed', icon: 'bx:support' },
    { key: 'at_risk', label: 'At risk', icon: 'bx:error' },
  ],
  discarded: [
    { key: 'not_interested', label: 'Not interested', icon: 'bx:dislike' },
    { key: 'wrong_person', label: 'Wrong person', icon: 'bx:user-x' },
    { key: 'out_of_icp', label: 'Out of ICP', icon: 'bx:target-lock' },
    { key: 'bad_timing', label: 'Bad timing', icon: 'bx:time-five' },
    { key: 'no_response', label: 'No response', icon: 'bx:message-x' },
    { key: 'bounced', label: 'Bounced', icon: 'bx:mail-send' },
  ],
};

export const SALES_OUTCOMES: OutcomeDef[] = [
  { key: 'waiting', label: 'Waiting' },
  { key: 'out_of_office', label: 'Out of office' },
  { key: 'nurture', label: 'Nurture' },
  { key: 'no_show', label: 'No-show' },
  { key: 'not_interested', label: 'Not interested' },
  { key: 'wrong_person', label: 'Wrong person' },
  { key: 'competitor', label: 'Competitor chosen' },
  { key: 'out_of_icp', label: 'Out of ICP' },
  { key: 'bad_timing', label: 'Bad timing' },
  { key: 'budget_freeze', label: 'Budget freeze' },
  { key: 'no_response', label: 'No response' },
  { key: 'bounced', label: 'Bounced' },
];

export const getStatusDef = (key: string): LeadStatusDef =>
  LEAD_STATUS[key as LeadStatusKey] || LEAD_STATUS.leads;
